/**
 * Property-scoped timezone helpers.
 *
 * Every scheduled task, article timestamp, and toast in the app should
 * be expressed in the ACTIVE PROPERTY's newsroom timezone — never the
 * browser's — so an editor in Mexico City sees the same schedule
 * grid as one in Ensenada. The backend stores every timestamp as UTC
 * ISO strings; this module is the one place where UTC ↔ property-tz
 * conversions live.
 *
 * We deliberately avoid pulling in date-fns-tz or dayjs/plugin/timezone
 * — the JavaScript standard `Intl.DateTimeFormat` covers everything we
 * need with zero bundle cost. The only trick is turning a
 * `datetime-local`-style wall-clock string into UTC, which we do by
 * measuring the target zone's offset at the exact instant the user
 * picked (so DST transitions work correctly).
 */

// A hand-picked list of timezones an editor might actually pick, biased
// toward the Americas since the customer base is Baja California and
// the neighbouring US Pacific / Mountain time cities. `Intl.supportedValuesOf`
// would give ~400 IANA zones — overwhelming and mostly irrelevant. We
// keep this list short + editable rather than dynamic.
export const COMMON_TIMEZONES = [
  { value: "America/Tijuana", label: "Tijuana / Baja California (PT)" },
  { value: "America/Mazatlan", label: "Mazatlán / Baja California Sur (MST)" },
  { value: "America/Mexico_City", label: "Mexico City (CT)" },
  { value: "America/Cancun", label: "Cancún / Quintana Roo (EST)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PT)" },
  { value: "America/Denver", label: "Denver (MT)" },
  { value: "America/Phoenix", label: "Phoenix (MST no DST)" },
  { value: "America/Chicago", label: "Chicago (CT)" },
  { value: "America/New_York", label: "New York (ET)" },
  { value: "America/Toronto", label: "Toronto (ET)" },
  { value: "America/Vancouver", label: "Vancouver (PT)" },
  { value: "America/Anchorage", label: "Anchorage (AKT)" },
  { value: "Pacific/Honolulu", label: "Honolulu (HST)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Madrid", label: "Madrid (CET)" },
  { value: "UTC", label: "UTC" },
];

const DEFAULT_TZ = "America/Tijuana";

/**
 * Extract the effective timezone for the current property.
 * Falls back to the shipped default when settings is missing / blank.
 * NEVER falls back to the browser tz — that would defeat the purpose
 * of a property-scoped timezone (an editor travelling would see their
 * schedule shift on landing).
 */
export function propertyTimezone(settings) {
  return (settings?.timezone || "").trim() || DEFAULT_TZ;
}

/**
 * Given a Date instant, return the target timezone's UTC offset in
 * minutes at that instant (positive = ahead of UTC, negative = behind).
 * Handles DST correctly because it queries `Intl.DateTimeFormat` for
 * the wall-clock at that exact moment.
 */
function tzOffsetMinutes(instant, tz) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(instant);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  // Intl uses hour "24" instead of "00" for midnight in some builds; wrap.
  let h = get("hour");
  if (h === 24) h = 0;
  const wallUtcMs = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    h,
    get("minute"),
    get("second"),
  );
  return (wallUtcMs - instant.getTime()) / 60000;
}

/**
 * Convert a `datetime-local` input value ("YYYY-MM-DDTHH:mm") to a
 * UTC ISO 8601 string, interpreting the wall-clock time as being in
 * the given timezone.
 *
 * Example: user types "2026-03-15T14:00" and tz is "America/Tijuana"
 * (PDT at that date, UTC-7) → returns "2026-03-15T21:00:00.000Z".
 *
 * DST-safe: we compute the tz offset using the *interpreted* instant
 * (not `Date.now()`), so scheduling "March 8 at 3am" during the
 * spring-forward hour still lands correctly.
 */
export function localInputToUtcIso(local, tz) {
  if (!local) return "";
  // Pretend the wall-clock string is UTC for a first-pass instant.
  const pretendUtc = new Date(`${local}:00Z`);
  if (Number.isNaN(pretendUtc.getTime())) return "";
  const offset = tzOffsetMinutes(pretendUtc, tz);
  // Real UTC = wall-clock (in tz) - offset. Since pretendUtc is the
  // wall clock read as if it were UTC, subtract the offset in ms.
  return new Date(pretendUtc.getTime() - offset * 60000).toISOString();
}

/**
 * Reverse of `localInputToUtcIso` — take a UTC ISO string and produce
 * the "YYYY-MM-DDTHH:mm" wall-clock string for the given timezone,
 * suitable for a `<input type="datetime-local">` value.
 */
export function utcIsoToLocalInput(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  let hh = get("hour");
  if (hh === "24") hh = "00";
  return `${get("year")}-${get("month")}-${get("day")}T${hh}:${get("minute")}`;
}

/**
 * Format a UTC ISO string for display in the property's timezone.
 * `style` selects between the compact and full-length presentations
 * used across the UI. Callers that need custom options can pass them
 * through `opts`.
 */
export function formatInTz(iso, tz, style = "full", opts = {}) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const base =
    style === "compact"
      ? {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        }
      : {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        };
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    ...base,
    ...opts,
  }).format(d);
}

/**
 * Short tz abbreviation for the given zone at the current moment —
 * "PT", "MST", "CDT", etc. Handy for tiny inline hints in the schedule
 * UI. Falls back to the raw IANA name if the runtime can't produce an
 * abbreviation (some minimal ICU builds).
 */
export function tzAbbreviation(tz, at = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
}
